import React, { useId, useState } from 'react';
import { Box, Collapse, Link, Typography } from '@mui/material';

import { GT_COLORS } from '../groups-teams/shared/constants';
import { AlertExplainer } from './alert-type-explainers';

/**
 * One row of the Notifications page: a label on the left, its controls on the
 * right, and, when an explainer is given, a "Learn more" link under the label
 * that opens a short explanation inside the row.
 *
 * Closed by default, so the page at rest shows only the choices. Each row opens
 * on its own, so someone comparing two alert types can have both open.
 */

interface Props {
    label: string;
    hint?: string;
    explainer?: AlertExplainer;
    children: React.ReactNode;
}

const Part = ({ heading, text }: { heading: string; text: string }) => (
    <Box>
        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: GT_COLORS.textSecondary }}>{heading}</Typography>
        <Typography sx={{ fontSize: '13px', lineHeight: 1.55, color: GT_COLORS.textSecondary }}>{text}</Typography>
    </Box>
);

export const NotificationRow: React.FC<Props> = ({ label, hint, explainer, children }) => {
    const [open, setOpen] = useState(false);
    const panelId = useId();

    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '170px minmax(0, 1fr)' },
            gap: '8px 20px',
            padding: '18px 0',
            '& + &': { borderTop: `1px solid ${GT_COLORS.borderLight}` },
        }}>
            <Box sx={{ paddingTop: { sm: '8px' } }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{label}</Typography>
                {hint && <Typography sx={{ fontSize: '12px', color: GT_COLORS.textMuted }}>{hint}</Typography>}
                {explainer && (
                    <Link
                        component="button"
                        type="button"
                        underline="hover"
                        aria-expanded={open}
                        aria-controls={panelId}
                        onClick={() => setOpen((v) => !v)}
                        sx={{ fontSize: '12px', marginTop: '2px' }}
                    >
                        {open ? 'Show less' : 'Learn more'}
                    </Link>
                )}
            </Box>

            <Box sx={{ display: 'grid', gap: '10px', minWidth: 0 }}>
                {children}
                {explainer && (
                    <Collapse in={open} unmountOnExit>
                        <Box
                            id={panelId}
                            role="region"
                            aria-label={`About ${label}`}
                            sx={{
                                display: 'grid',
                                gap: '10px',
                                maxWidth: 560,
                                padding: '12px 14px',
                                borderRadius: '8px',
                                border: `1px solid ${GT_COLORS.border}`,
                                backgroundColor: GT_COLORS.headerBg,
                            }}
                        >
                            <Part heading="What it is" text={explainer.whatItIs} />
                            <Part heading="What happens" text={explainer.whatHappens} />
                            <Part heading="What you’re choosing" text={explainer.whatYoureChoosing} />
                        </Box>
                    </Collapse>
                )}
            </Box>
        </Box>
    );
};
